import sectionFields from 'molecules/Section/config';

import type { Component, Input } from '@builder.io/sdk';
import type { WistiaFormReference } from 'components/WistiaForm/config';

/**
 * Above-the-fold event / workshop hero: eyebrow, heading, subhead, a date/time
 * callout, and a registration form (Marketo or Wistia, chosen in Builder).
 */
const eventRegistrationHeroFields = [
  {
    name: 'eyebrow',
    type: 'string',
    defaultValue: 'PRISMATIC LABS',
    helperText: 'Short uppercase label above the heading (e.g. "PRISMATIC LABS").',
  },
  {
    name: 'heading',
    type: 'string',
    defaultValue: 'Watch AI Migrate Real Integration Flows Into Prismatic, Live',
    helperText: 'Main heading. Supports <br /> for manual line breaks.',
  },
  {
    name: 'body',
    type: 'longText',
    helperText: 'Supporting subhead shown below the heading.',
  },
  {
    name: 'calloutLabel',
    type: 'string',
    defaultValue: 'Live Workshop',
    helperText: 'Label shown in the callout box (e.g. "Live Workshop" or "On-Demand Workshop").',
  },
  {
    name: 'eventDate',
    type: 'string',
    helperText: 'Optional. Shown with a calendar icon in the callout (e.g. "August 19, 2026"). Hidden when empty.',
  },
  {
    name: 'eventTime',
    type: 'string',
    helperText: 'Optional. Shown with a clock icon in the callout (e.g. "2 PM EST"). Hidden when empty.',
  },
  {
    name: 'formTool',
    type: 'string',
    enum: ['marketo', 'wistia'],
    defaultValue: 'marketo',
    helperText: 'Which form tool to embed on the right. Pick the matching reference field below.',
  },
  {
    name: 'formHeadline',
    type: 'string',
    showIf: "options.get('formTool') === 'wistia'",
    helperText:
      'Optional title shown above the form. Only for Wistia (Marketo forms bring their own heading). Wistia forms have no built-in heading, so set one here if you want a title.',
  },
  {
    name: 'formLayout',
    type: 'string',
    enum: ['grid', 'inline'],
    defaultValue: 'grid',
    helperText:
      'Field layout inside the form. "grid" places two fields per row (First/Last side by side); "inline" stacks every field full-width. Applies to Marketo and Wistia.',
  },
  {
    name: 'marketoForm',
    type: 'reference',
    model: 'marketo-forms',
    showIf: "options.get('formTool') === 'marketo'",
    helperText: 'Select a Marketo form to embed. Used when the form tool is "marketo".',
  },
  {
    name: 'wistiaForm',
    type: 'reference',
    model: 'wistia-forms',
    showIf: "options.get('formTool') === 'wistia'",
    helperText:
      'Select a Wistia form/live-event entry (from the "wistia-forms" data model). Used when the form tool is "wistia".',
  },
  {
    name: 'redirectUrl',
    type: 'url',
    helperText:
      'Optional thank-you URL. For Wistia, the visitor is redirected here after submitting (UTM params carried over). Marketo forms use their own configured redirect.',
  },

  ...sectionFields,
] as const satisfies DeepReadonly<Input[]>;

const eventRegistrationHeroConfig = {
  name: 'EventRegistrationHero',
  inputs: eventRegistrationHeroFields,
} satisfies Component;

export type EventRegistrationHeroProps = Omit<
  GenerateBuilder<typeof eventRegistrationHeroFields>,
  'marketoForm' | 'wistiaForm'
> & {
  marketoForm?: FormReference;
  wistiaForm?: WistiaFormReference;
};

export default eventRegistrationHeroConfig;
